<template>
  <form
    id="indicator-form"
    @submit.prevent="$emit('submit')"
  >
    <TextInput
      v-model="formData.name"
      label="Indicator Name"
      :validation-error="error"
      class="!max-w-none"
      required
    />

    <TextEditorInput
      id="modal-indicator-description"
      v-model="formData.description"
      :inline="false"
      :allow-image-upload="false"
      class="!mt-6"
      label="Indicator Description"
    />

    <div class="w-full">
      <AppTable class="w-full leading-normal">
        <AppTableHeader class="bg-editor-table-header-bg">
          <th>Baseline</th>

          <template
            v-for="year in 5"
            :key="year"
          >
            <th>
              <span class="block">Year {{ year }}</span>
              target
            </th>

            <th v-if="year < 5">
              <span class="block">Year {{ year }}</span>
              actual
            </th>
          </template>
        </AppTableHeader>

        <AppTableBody>
          <tr>
            <td>
              <textarea
                ref="textareas"
                v-model="formData.indicatorValues.baseline"
                rows="1"
                @input="syncHeights"
                @keydown="handleTdKeydown"
                @paste="handleTdPaste"
              />

              <small
                v-if="counts.baseline > 10"
                :class="counts.baseline >= TD_WORD_LIMIT ? 'text-danger' : 'text-black'"
              >
                {{ counts.baseline }} / {{ TD_WORD_LIMIT }}
              </small>
            </td>

            <template
              v-for="year in 5"
              :key="year"
            >
              <td>
                <textarea
                  ref="textareas"
                  v-model="formData.indicatorValues[`year${year}`].target"
                  rows="1"
                  @input="syncHeights"
                  @keydown="handleTdKeydown"
                  @paste="handleTdPaste"
                />

                <small
                  v-if="counts[`year${year}`].target > 10"
                  :class="counts[`year${year}`].target >= TD_WORD_LIMIT ? 'text-danger' : 'text-black'"
                >
                  {{ counts[`year${year}`].target }} / {{ TD_WORD_LIMIT }}
                </small>
              </td>

              <td v-if="year < 5">
                <textarea
                  ref="textareas"
                  v-model="formData.indicatorValues[`year${year}`].actual"
                  rows="1"
                  @input="syncHeights"
                  @keydown="handleTdKeydown"
                  @paste="handleTdPaste"
                />

                <small
                  v-if="counts[`year${year}`].actual > 10"
                  :class="counts[`year${year}`].actual >= TD_WORD_LIMIT ? 'text-danger' : 'text-black'"
                >
                  {{ counts[`year${year}`].actual }} / {{ TD_WORD_LIMIT }}
                </small>
              </td>
            </template>
          </tr>
        </AppTableBody>
      </AppTable>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';

import { TextEditorInput, TextInput } from '@/components/form-inputs';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { useToasts } from '@/composables';

const toasts = useToasts();

defineEmits(['submit']);

defineProps({
  error: {
    type: String,
    default: null,
  },
});

const formData = defineModel({
  default: () => ({
    name: null,
    description: null,
    indicatorValues: {
      baseline: null,
      year1: { target: null, actual: null },
      year2: { target: null, actual: null },
      year3: { target: null, actual: null },
      year4: { target: null, actual: null },
      year5: { target: null, actual: null },
    },
  }),
});

const textareas = ref([]);
const TD_WORD_LIMIT = 60;

const wordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const counts = computed(() => {
  const iv = formData.value.indicatorValues;
  return {
    baseline: wordCount(iv.baseline),
    ...Object.fromEntries(
      [1, 2, 3, 4, 5].map(y => [`year${y}`, {
        target: wordCount(iv[`year${y}`]?.target),
        actual: wordCount(iv[`year${y}`]?.actual),
      }]),
    ),
  };
});

const handleTdKeydown = (e) => {
  const allowedKeys = [8, 46, 37, 38, 39, 40];
  if (allowedKeys.includes(e.keyCode) || e.ctrlKey || e.metaKey) return;

  const count = wordCount(e.target.value);
  const isSeparator = e.key === ' ' || e.keyCode === 32 || e.keyCode === 13;

  if (count > TD_WORD_LIMIT || (count === TD_WORD_LIMIT && isSeparator)) {
    e.preventDefault();
    e.stopPropagation();
  }
};

const handleTdPaste = (e) => {
  e.preventDefault();

  const pastedText = e.clipboardData?.getData('text/plain') || '';

  if (wordCount(e.target.value) + wordCount(pastedText) > TD_WORD_LIMIT) {
    toasts.error('Word limit reached.');
    return;
  }

  document.execCommand('insertText', false, pastedText);
};

const syncHeights = () => {
  // Preserve modal scroll position — resetting heights to 'auto' causes a
  // momentary collapse that makes the browser scroll to keep the focused element in view
  const modalBody = textareas.value[0]?.closest('.modal-body');
  const savedScroll = modalBody?.scrollTop ?? 0;

  textareas.value.forEach(el => {
    el.style.height = 'auto';
    el.parentElement.style.height = 'auto';
  });

  const maxHeight = Math.max(...textareas.value.map(el => el.scrollHeight));

  textareas.value.forEach(el => {
    el.style.height = `${maxHeight}px`;
    el.parentElement.style.height = `${maxHeight}px`;
  });

  if (modalBody) modalBody.scrollTop = savedScroll;
};

onMounted(() => nextTick(syncHeights));
</script>

<style scoped>
tbody {
  tr {
    td {
      @apply relative;
    }

    textarea {
      @apply text-start;
    }

    td:not(:first-child) {
      textarea {
        @apply w-full outline-none resize-none overflow-hidden border-b border-b-editor-table-border;
      }
    }

    small {
      @apply absolute -bottom-6 right-2;
    }
  }
}
</style>
